import {
    assoc,
    clone,
    filter,
    flatten,
    fromPairs,
    has,
    hasPath,
    identity,
    ifElse,
    indexBy,
    lens,
    lensProp,
    map,
    omit,
    over,
    path,
    pathOr,
    pipe,
    prepend,
    prop,
    sortBy,
    when
} from 'ramda';
import getCashierConfig from './cashierConfig';
import parseMethod from 'js/components/main/Cashier/parseMethod';

// Methods will have:
// `accountFields` - account fields descriptors. Plain array of object without `field` prop.

// Accounts will have:
// `accountData` - values of account fields corresponding to `accountFields`. Plain object of key/value pairs without
// intermediate `account` prop. Populated from `accountFields.account` array and `specificParams`.
// Those props are removed.

export const getList = pathOr([], ['data', 'payments', 'method']);

export const reformatMethodProps = over(
    lens(
        path(['accountFields', 'field']),
        assoc('accountFields')
    ),
    identity
);

export const getAccountData = pipe(
    ifElse(
        hasPath(['accountFields', 'field']),
        over(
            lens(path(['accountFields', 'field']), assoc('accountData')),
            pipe(
                map(({ name, value }) => [name, value]),
                fromPairs
            )
        ),
        when(
            has('specificParams'),
            over(
                lens(prop('specificParams'), assoc('accountData')),
                clone
            )
        )
    ),
    omit(['accountFields', 'specificParams'])
);

export const reformatAccountData = when(
    has('account'),
    over(
        lensProp('account'),
        map(getAccountData)
    )
);

export const prepareAccounts = accounts => pipe(
    map(reformatAccountData),
    indexBy(prop('code')),
    map(({ account }) => [...account])
)(accounts);

export const comparator = ({ paymentMethodsOrder = 'byCasinoConfiguration' }) =>
    pathOr(999, ['ordering', paymentMethodsOrder]);

export const addCommonFields = assoc('isSubMethod', false);

export const sortSkippedSubMethods = options => method => ({
    ...method,
    subMethods: {
        subMethod: sortBy(
            comparator(options),
            method.subMethods.subMethod
        )
    }
});

export const getSubMethods = ({ subMethods, ...rest }) => (method) => {
    if (!method.subMethods) {
        return method;
    }

    const { code: methodCode } = method;
    const skippedSubMethods = ['DotpayBankTransfer', 'PayPalViaSC'];

    if (skippedSubMethods.includes(methodCode)) {
        return sortSkippedSubMethods(rest)(method);
    }

    subMethods[methodCode] = pipe(
        path(['subMethods', 'subMethod']),
        map((subMethod) => {
            const { code, name, ordering } = subMethod;

            return {
                ...omit(['subMethods'], method),
                code,
                name,
                ordering,
                methodCode,
                isSubMethod: true
            };
        })
    )(method);

    return method;
};

export const addSubMethods = ({ subMethods }) =>
    pipe(
        map(method => [method, subMethods[method.code] || []]),
        flatten
    );

export const filterParentMethods = options => (method) => {
    const { hideParentPaymentMethods, subMethods } = options;

    return !(hideParentPaymentMethods && method.hideParentMethod && subMethods[method.code]);
};

export const mergeWithAccountFields = accounts => method => ({
    ...method,
    accounts: accounts[method.code] || []
});

export const filterAccounts = when(
    ({ accounts }) => Boolean(accounts),
    method => ({
        ...method,
        accounts: method.accounts
            .filter(acc => acc.view === true && acc.accountData != null)
    })
);

export const isVisaCheckoutEnabled = (options) => {
    const { enableVisaCheckout, tokenProvider } = options;

    return enableVisaCheckout && tokenProvider === 'safecharge';
};

export const extractVisaCheckoutCC = options => (method) => {
    const { VCOAccounts } = options;
    const { accounts } = method;

    if (!isVisaCheckoutEnabled(options) || !accounts) {
        return method;
    }

    return {
        ...method,
        accounts: accounts.filter((item) => {
            const visaCheckout = item.accountFields && item.accountFields.filter(
                ({ name }) => name === 'visaCheckout'
            )[0];

            const {
                code: pmCode,
                limits,
                suggested,
                amountRequired,
                validationConfig
            } = method;

            if (visaCheckout) {
                VCOAccounts.push({
                    ...item,
                    pmCode,
                    limits,
                    suggested,
                    amountRequired,
                    validationConfig
                });

                return false;
            }

            return true;
        })
    };
};

export const addVisaCheckoutPM = ({ VCOAccounts: accounts }) =>
    when(
        () => accounts.length > 0,
        prepend({
            accounts,
            name: 'Visa Checkout',
            code: 'VisaCheckout',
            ordering: {
                byPlayerHistory: 1,
                byCountryPopularity: 1,
                byCasinoConfiguration: 1
            }
        })
    );

export const removeCCMethodsWithoutAccounts = options => (method) => {
    const { paymentType } = options;
    const { code, accounts } = method;
    const config = getCashierConfig({ paymentType, code });

    return !config
        || !config.isCreditCard
        || Boolean(accounts && accounts.length);
};

// todo: need for withdraw collection
export const addPredefinedOptions = method => method;

export default options => (response) => {
    let [methods, accounts] = response.map(getList);
    methods = methods.map(reformatMethodProps);
    accounts = prepareAccounts(accounts);

    const VCOAccounts = [];
    const subMethods = {};

    return pipe(
        sortBy(comparator(options)),
        map(addCommonFields),
        map(getSubMethods({ ...options, subMethods })),
        addSubMethods({ subMethods }),
        filter(filterParentMethods({ ...options, subMethods })),
        map(mergeWithAccountFields(accounts)),
        map(filterAccounts),
        map(extractVisaCheckoutCC({ ...options, VCOAccounts })),
        addVisaCheckoutPM({ VCOAccounts }),
        filter(removeCCMethodsWithoutAccounts(options)),
        map(addPredefinedOptions),
        map(parseMethod(options))
    )(methods);
};
